/**
 * The swap's data (21/10/2026): the read (what the container writes with,
 * the entry that failed, where a swap lands) while the panel is open, and
 * the swap — then the texts are filled again and the errors read again.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DropGenPromptSwapBody, DropGenPromptSwapView } from '@trafficpulse/shared';

import { api } from '@/services/api';

import { useDropGeneratorMutations } from '../../../../useDropGeneratorMutations';
import { dropGeneratorKeys } from '../../../../useDropGenerator';

interface Envelope<T> {
  data: T;
}

const url = (countryKey: string, errorId: string) => `/drop-generator/errors/${countryKey}/${errorId}/prompt-swap`;
const key = (errorId: string) => [...dropGeneratorKeys.all, 'errors', 'prompt-swap', errorId] as const;

export function usePromptSwapView(countryKey: string, errorId: string, enabled: boolean) {
  return useQuery({
    queryKey: key(errorId),
    queryFn: async () => (await api.get<Envelope<DropGenPromptSwapView>>(url(countryKey, errorId))).data.data,
    enabled,
    staleTime: 0,
    retry: false,
  });
}

/** What came of it: the swap is saved; queued = a fill was running, the texts are filled again once it ends; fillError = the fill could not start. */
export interface SwapOutcome {
  view: DropGenPromptSwapView;
  queued: boolean;
  fillError: unknown | null;
}

/** Swap, then fill the texts again (the error resolves once the drafts have their texts). A fill already running is not started over — the server fills again once it ends; a fill that cannot start never hides a saved swap. */
export function usePromptSwap(countryKey: string, errorId: string) {
  const qc = useQueryClient();
  const { startCreativeFill } = useDropGeneratorMutations();
  return useMutation({
    mutationFn: async (body: DropGenPromptSwapBody): Promise<SwapOutcome> => {
      const view = (await api.post<Envelope<DropGenPromptSwapView>>(url(countryKey, errorId), body)).data.data;
      if (view.swapped?.refill) return { view, queued: true, fillError: null };
      const fillError = await startCreativeFill.mutateAsync(countryKey).then(
        () => null,
        (err: unknown) => err,
      );
      return { view, queued: false, fillError };
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: [...dropGeneratorKeys.all, 'errors'] }),
  });
}
